import React, { ReactElement, useEffect } from 'react';
import { Params, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Dispatch } from 'redux';

import { Collection, Resource } from '../../../types/entity.types';
import { Document } from '../../../types/document.types';
import { RootState } from '../../../types/state/root.types';
import { PlanState } from '../../../types/state/plan.types';
import { MemberState } from '../../../types/state/member.types';

import { downloadFile } from '../../../utils/util';

import {
  requestCreateCollection,
  requestDeleteCollection,
  requestUpdateCollection,
  requestViewAllCollections
} from '../../../api/collection/collection.actions';
import {
  requestCreateResource,
  requestUpdateResource,
  requestDeleteResource
} from '../../../api/resource/resource.actions';
import { requestViewProgramme } from '../../../api/programme/programme.actions';
import { requestDownloadDocument, requestUploadDocument } from '../../../api/document/document.actions';
import { requestViewAllMembers } from '../../../api/member/member.actions';

import Plan from '../../../components/screens/programme/plan/Plan';

const PlanContainer = (): ReactElement => {
  const { collections } = useSelector(({ plan }: RootState): PlanState => plan);
  const { members } = useSelector(({ member }: RootState): MemberState => member);

  const dispatch: Dispatch = useDispatch();

  const { status, programmeExternalReference }: Params = useParams();

  useEffect((): void => {
    if (programmeExternalReference) {
      // @ts-expect-error TODO: Fix after adding types to programme actions
      dispatch(requestViewProgramme({ externalReference: programmeExternalReference }));
      // @ts-expect-error TODO: Fix after adding types to collection actions
      dispatch(requestViewAllCollections({ externalReference: programmeExternalReference }));
      // @ts-expect-error TODO: Fix after adding types to member actions
      dispatch(requestViewAllMembers({ externalReference: programmeExternalReference }));
    }
  }, [programmeExternalReference, dispatch]);

  const createCollection = (collection: Collection, onSuccess: (externalReference: string) => void): void => {
    dispatch(
      // @ts-expect-error TODO: Fix after adding types to collection actions
      requestCreateCollection({
        input: {
          programmeExternalReference,
          collection
        },
        onSuccess
      })
    );
  };

  const updateCollection = (collection: Collection, onSuccess?: (externalReference: string) => void): void => {
    const updatedCollection: Collection = { ...collection };
    updatedCollection.programmeExternalReference = programmeExternalReference;
    dispatch(
      // @ts-expect-error TODO: Fix after adding types to collection actions
      requestUpdateCollection({
        input: {
          collection: updatedCollection
        },
        programmeExternalReference,
        onSuccess
      })
    );
  };

  const deleteCollection = (externalReference: string): void => {
    dispatch(
      // @ts-expect-error TODO: Fix after adding types to collection actions
      requestDeleteCollection({
        externalReference,
        programmeExternalReference
      })
    );
  };

  const uploadDocument = async (document: Document): Promise<string> => {
    return new Promise((resolve: (value: string | PromiseLike<string>) => void): void => {
      dispatch(
        // @ts-expect-error TODO: Fix after adding types to document actions
        requestUploadDocument({
          document,
          onSuccess: (documentExternalReference: string): void => {
            resolve(documentExternalReference);
          }
        })
      );
    });
  };

  const createResource = async (
    collectionExternalReference: string,
    resource: Resource,
    onSuccess: () => void
  ): Promise<void> => {
    if (resource.document) {
      const documentExternalReference: string = await uploadDocument(resource.document);
      resource = { ...resource, documentExternalReference };
      delete resource.document;
    }

    dispatch(
      // @ts-expect-error TODO: Fix after adding types to resource actions
      requestCreateResource({
        input: {
          collectionExternalReference,
          resource,
          programmeExternalReference
        },
        programmeExternalReference,
        onSuccess
      })
    );
  };

  const updateResource = async (resource: Resource, onSuccess?: () => void): Promise<void> => {
    if (resource.document) {
      const documentExternalReference: string = await uploadDocument(resource.document);
      resource = { ...resource, documentExternalReference };
      delete resource.document;
    }

    dispatch(
      // @ts-expect-error TODO: Fix after adding types to resource actions
      requestUpdateResource({
        input: {
          collectionExternalReference: resource.collectionExternalReference,
          resource,
          programmeExternalReference
        },
        programmeExternalReference,
        onSuccess
      })
    );
  };

  const deleteResource = (collectionExternalReference: string, resourceExternalReference: string): void => {
    dispatch(
      // @ts-expect-error TODO: Fix after adding types to resource actions
      requestDeleteResource({
        programmeExternalReference,
        collectionExternalReference,
        resourceExternalReference
      })
    );
  };

  const downloadDocument = (resource: Resource): void => {
    const { documentExternalReference, name } = resource;

    dispatch(
      // @ts-expect-error TODO: Fix after adding types to document actions
      requestDownloadDocument({
        externalReference: documentExternalReference,
        onSuccess: (data: unknown): void => downloadFile(data, name)
      })
    );
  };

  const sortCollections = (): Collection[] => {
    const sortedCollections: Collection[] = [...collections].sort(
      (collectionOne: Collection, collectionTwo: Collection): number => {
        const nameOne: string = collectionOne.name.toLowerCase();
        const nameTwo: string = collectionTwo.name.toLowerCase();

        if (nameOne > nameTwo) return 1;
        if (nameOne < nameTwo) return -1;
        return 0;
      }
    );
    return sortedCollections;
  };

  return (
    <Plan
      status={status as string}
      collections={sortCollections()}
      totalMembers={members.length}
      createCollection={createCollection}
      updateCollection={updateCollection}
      deleteCollection={deleteCollection}
      createResource={createResource}
      updateResource={updateResource}
      deleteResource={deleteResource}
      downloadDocument={downloadDocument}
    />
  );
};

export default PlanContainer;
